"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { excursionTerms } from "@/lib/excursionTerms";
export default function TermsConditions() {
  const deExcursions = excursionTerms.de;
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1,
    once: true
  });
  return <section id="terms-conditions" data-nav="light" ref={ref} className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-opacity duration-700 ease-out" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="max-w-[1136px] mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Main content spans columns 3 to 10 (centered readable column) */}
          <div className="md:col-start-3 md:col-span-8">
            {/* Header */}
            <div className="mb-8">
              <h1 className="font-default text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
                Allgemeine Geschäftsbedingungen
              </h1>

              {/* Signature hairline rule capped with turquoise terminal dot */}
              <div className="relative flex items-center mb-6" aria-hidden="true">
                <div className="h-px bg-border flex-1" />
                <div className="size-2 rounded-full bg-primary shrink-0 -ml-1" />
              </div>

              <p className="text-sm font-medium text-muted-foreground">
                Zuletzt aktualisiert: 18. Oktober 2026
              </p>
              <p className="text-sm font-medium text-muted-foreground mt-1">
                Betreiber: Ahmed Kamal Hussein Abdel-Aal.
              </p>
            </div>

            {/* Continuous Legal Text */}
            <div className="space-y-6 text-base sm:text-lg leading-relaxed text-foreground/90">
              <p className="text-foreground">
                Willkommen bei Hurghada Transfer Hub. Mit der Buchung eines Transfers über unsere Website, per Telefon oder WhatsApp stimmen Sie den folgenden Geschäfts- und Betriebsbedingungen zu.
              </p>

              <div className="rounded-md border border-border bg-card p-6 md:p-8 space-y-6 shadow-xs">
                <article className="space-y-4">
                  <h2 className="text-base sm:text-lg font-semibold text-foreground">
                    1. Buchung &amp; Bestätigung
                  </h2>
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                      Transfers
                    </h3>
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      Online oder per Nachricht vorgenommene Buchungen sind bestätigt, sobald unsere Disposition diese geprüft und eine Buchungsbestätigung ausgestellt hat. Wir behalten uns das Recht vor, Buchungen abzulehnen, wenn keine ausreichende Fahrzeugkapazität oder Routenverfügbarkeit gegeben ist.
                    </p>
                  </div>
                  <div className="space-y-2 pt-2 border-t border-border/50">
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                      Ausflüge
                    </h3>
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      Deine Ausflugsbuchung ist bestätigt, sobald deine gewählte Zahlung bei uns eingegangen ist: entweder die Online-Anzahlung von 50 % per Karte oder die vollständige Online-Kartenzahlung.
                    </p>
                  </div>
                </article>

                <div className="h-px bg-border" aria-hidden="true" />

                <article className="space-y-4">
                  <h2 className="text-base sm:text-lg font-semibold text-foreground">
                    2. Preise &amp; Zahlung
                  </h2>
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                      Transfers
                    </h3>
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      Die vereinbarten Preise gelten pro Fahrzeug. Wir bestätigen vor der Buchung, welche Kosten enthalten sind. Die Zahlung erfolgt bei Ankunft am Hotel in bar. Wir akzeptieren jede Währung; bitte vereinbaren Sie die Währung und den Betrag vor Ihrem Transfer mit uns.
                    </p>
                  </div>
                  <div className="space-y-3 pt-2 border-t border-border/50">
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                      Ausflüge
                    </h3>
                    <p className="text-muted-foreground text-xs sm:text-sm">
                      {deExcursions.scope}
                    </p>
                    <div className="space-y-1.5">
                      <p className="text-sm font-medium text-foreground">
                        {deExcursions.stepsTitle}:
                      </p>
                      <ol className="list-decimal list-inside space-y-1 text-muted-foreground text-sm sm:text-base leading-relaxed pl-1">
                        {deExcursions.steps.map((step, idx) => <li key={idx} className="pl-1">
                            {step}
                          </li>)}
                      </ol>
                    </div>
                    <div className="space-y-1 pt-1">
                      <p className="text-sm font-medium text-foreground">
                        {deExcursions.paymentTitle}
                      </p>
                      <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                        {deExcursions.paymentOptions}
                      </p>
                    </div>
                    <div className="space-y-1 pt-1">
                      <p className="text-sm font-medium text-foreground">
                        {deExcursions.pickupTitle}
                      </p>
                      <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                        {deExcursions.pickupCoverage}
                      </p>
                    </div>
                    <p className="text-muted-foreground text-xs sm:text-sm pt-1">
                      {deExcursions.guideLanguages}
                    </p>
                  </div>
                </article>

                <div className="h-px bg-border" aria-hidden="true" />

                <article className="space-y-2">
                  <h2 className="text-base sm:text-lg font-semibold text-foreground">
                    3. Flugverspätungen &amp; Wartezeit
                  </h2>
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                    Wir verfolgen die Ankunftszeit und mögliche Verspätungen deines Fluges kostenlos. Dein Transfer beinhaltet bis zu 60 Minuten kostenlose Wartezeit ab dem vereinbarten Treffzeitpunkt. Bitte gib bei der Buchung deine Flugnummer an, damit wir die Ankunft zuverlässig berücksichtigen können.
                  </p>
                </article>

                <div className="h-px bg-border" aria-hidden="true" />

                <article className="space-y-4">
                  <h2 className="text-base sm:text-lg font-semibold text-foreground">
                    4. Stornierungen &amp; Änderungen
                  </h2>
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                      Transfers
                    </h3>
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      Eine Stornierung ist bis spätestens 24 Stunden vor der vereinbarten Abhol- oder Treffzeit kostenlos. Bei einer späteren Stornierung fallen Stornogebühren in Höhe von 25 % des vereinbarten Gesamtbuchungspreises an. Bitte kontaktieren Sie unsere Disposition bei dringenden Änderungen direkt per WhatsApp.
                    </p>
                  </div>
                  <div className="space-y-2 pt-2 border-t border-border/50">
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                      Ausflüge
                    </h3>
                    <div className="space-y-1">
                      <p className="text-xs sm:text-sm font-medium text-foreground">
                        {deExcursions.cancellationTitle}
                      </p>
                      <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                        {deExcursions.cancellation}
                      </p>
                    </div>
                    <div className="space-y-1 pt-1">
                      <p className="text-xs sm:text-sm font-medium text-foreground">
                        {deExcursions.refundTitle}
                      </p>
                      <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                        {deExcursions.weatherRefund}
                      </p>
                    </div>
                  </div>
                </article>

                <div className="h-px bg-border" aria-hidden="true" />

                <article className="space-y-2">
                  <h2 className="text-base sm:text-lg font-semibold text-foreground">
                    5. Gepäck &amp; Fahrzeugrichtlinien
                  </h2>
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                    Fahrgäste müssen sicherstellen, dass Personenzahl und Gepäckmenge die für ihre gebuchte Fahrzeugkategorie angegebenen Grenzwerte nicht überschreiten. Wir behalten uns das Recht vor, die Beförderung zu verweigern oder ein größeres Zusatzfahrzeug zu verlangen, sollten die Gepäckgrenzen überschritten werden.
                  </p>
                </article>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}